import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  AnimatePresence,
} from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { fmtPct, fmtUSD } from "@/lib/format";
import useSmoothScroll from "@/lib/useSmoothScroll";

/* -----------------------------------------------------------------
   Neo.Invest — Awwwards-flavored landing page
   • Lenis momentum scroll
   • Framer-motion masked line-by-line hero reveal
   • Parallax on hero photography
   • Slow editorial marquee
   • Numbered manifesto chapters with scroll reveals
   • Real photography treated w/ clipped frames + spotlight hover
   ----------------------------------------------------------------- */

export default function Landing() {
  useSmoothScroll(true);
  const [tickers, setTickers] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const heroImgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const heroOverlay = useTransform(scrollYProgress, [0, 1], [0.65, 0.9]);

  // Custom cursor spotlight
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { damping: 30, stiffness: 300 });
  const sy = useSpring(my, { damping: 30, stiffness: 300 });

  useEffect(() => {
    const move = (e) => {
      mx.set(e.clientX);
      my.set(e.clientY);
    };
    window.addEventListener("pointermove", move);
    return () => window.removeEventListener("pointermove", move);
  }, [mx, my]);

  useEffect(() => {
    api
      .get("/markets/summary")
      .then(({ data }) => {
        const merged = [
          ...(data.top_crypto || []).slice(0, 10),
          ...(data.top_stocks || []).slice(0, 6),
          ...(data.top_bonds || []).slice(0, 4),
        ];
        setTickers(merged);
      })
      .catch(() => setTickers([]));
  }, []);

  return (
    <div className="min-h-screen bg-neo-bg text-white antialiased overflow-x-hidden">
      {/* Cursor spotlight — desktop only */}
      <motion.div
        aria-hidden
        className="pointer-events-none fixed z-[60] hidden md:block w-[420px] h-[420px] rounded-full"
        style={{
          x: sx,
          y: sy,
          translateX: "-50%",
          translateY: "-50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.08), rgba(255,255,255,0) 60%)",
          mixBlendMode: "screen",
        }}
      />

      <SiteHeader />

      {/* -------------------------------------------------------- HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative overflow-hidden min-h-[110vh] flex items-end"
      >
        <motion.div
          style={{ y: heroImgY, scale: heroImgScale }}
          className="absolute inset-0"
        >
          <img
            alt=""
            src="https://images.unsplash.com/photo-1642790551116-18e150f248e3?w=2000&q=70"
            className="w-full h-full object-cover grayscale contrast-125 opacity-60"
          />
        </motion.div>
        <motion.div
          className="absolute inset-0 bg-black"
          style={{ opacity: heroOverlay }}
        />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/95 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black to-transparent" />

        <div className="relative w-full max-w-7xl mx-auto px-6 pb-24 pt-40">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-9">
              <MetaLine>// n01 — capital markets, unified terminal</MetaLine>
              <h1 className="mt-8 text-[13vw] md:text-[9vw] lg:text-[8.2vw] leading-[0.85] uppercase font-black tracking-[-0.03em]">
                <MaskReveal delay={0.1}>Trade every</MaskReveal>
                <MaskReveal delay={0.25} accent>
                  market
                </MaskReveal>
                <MaskReveal delay={0.4}>from one book.</MaskReveal>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 max-w-xl text-neutral-300 text-base md:text-lg leading-relaxed"
              >
                A single ledger for crypto, equities and treasuries. Fund with Stripe or
                PayPal, keep your <span className="text-white font-semibold">$100 welcome bonus</span>,
                and settle every trade at a flat <span className="font-mono">1%</span>. No custodians.
                No spread. No noise.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
                className="mt-12 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/register"
                  data-testid="hero-primary-cta"
                  className="group relative inline-flex items-center gap-3 bg-white text-black px-7 py-4 font-mono uppercase tracking-widest text-[11px] border border-white overflow-hidden"
                >
                  <span className="relative z-10">Claim $100 bonus</span>
                  <ArrowUpRight
                    className="relative z-10 w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={1.75}
                  />
                  <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                  <span className="absolute inset-0 mix-blend-difference text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-3 px-7">
                    Claim $100 bonus <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                  </span>
                </Link>
                <Link
                  to="/login"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 font-mono uppercase tracking-widest text-[11px] hover:border-white hover:bg-white/5 transition-colors"
                >
                  Sign in
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-3 relative"
            >
              <div className="border-l border-white/25 pl-6">
                <MetaLine>// live_feed</MetaLine>
                <div className="mt-4 font-mono text-4xl font-black tracking-tighter leading-none">
                  {String(tickers.length || 280).padStart(3, "0")}
                </div>
                <div className="mt-2 text-xs text-neutral-400 uppercase tracking-widest">
                  assets streaming
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.6 }}
          className="absolute bottom-6 right-6 z-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400"
        >
          <span>scroll</span>
          <motion.span
            className="block w-px h-8 bg-white/30"
            animate={{ scaleY: [0.2, 1, 0.2] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "top" }}
          />
        </motion.div>
      </section>

      {/* ------------------------------------------------- TICKER MARQUEE */}
      <MarqueeTicker tickers={tickers} />

      {/* ------------------------------------------------- MANIFESTO */}
      <Manifesto />

      {/* ------------------------------------------------- IMAGE GRID */}
      <ProductGallery />

      {/* ------------------------------------------------- BIG NUMBER */}
      <BigStatement />

      {/* ------------------------------------------------- PRICING */}
      <Pricing />

      {/* ------------------------------------------------- COLOPHON */}
      <Colophon />
    </div>
  );
}

/* =====================================================
   REVEAL PRIMITIVES
   ===================================================== */

const easeOutExpo = [0.22, 1, 0.36, 1];

function MetaLine({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.6 }}
      className="font-mono text-[10px] uppercase tracking-[0.4em] text-neutral-400"
    >
      {children}
    </motion.div>
  );
}

/** Line-by-line masked hero reveal */
function MaskReveal({ children, delay = 0, accent = false }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, delay, ease: easeOutExpo }}
        className={`block ${accent ? "italic text-white/90" : ""}`}
      >
        {accent ? (
          <span className="inline-block relative">
            <span className="relative z-10 px-2">{children}</span>
            <span className="absolute inset-0 bg-white -skew-x-6" />
            <span className="absolute inset-0 mix-blend-difference text-white flex items-center justify-center px-2">
              {children}
            </span>
          </span>
        ) : (
          children
        )}
      </motion.span>
    </span>
  );
}

function RevealUp({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.9, delay, ease: easeOutExpo }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* =====================================================
   HEADER
   ===================================================== */

function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <motion.header
      data-testid="landing-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.05, ease: easeOutExpo }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-black/50 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <div className="w-6 h-6 border border-white flex items-center justify-center transition-transform group-hover:rotate-45">
            <span className="font-mono text-xs font-bold">N</span>
          </div>
          <span className="font-mono uppercase tracking-[0.28em] text-[11px]">Neo.Invest</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 font-mono uppercase tracking-[0.28em] text-[10px] text-neutral-300">
          <a href="#manifesto" className="hover:text-white transition-colors">Manifesto</a>
          <a href="#gallery" className="hover:text-white transition-colors">Terminal</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="header-login-link"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-neutral-300 hover:text-white transition-colors px-3 py-2"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="header-register-link"
            className="group relative inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] bg-white text-black px-4 py-2 border border-white overflow-hidden"
          >
            <span className="relative z-10">Open account</span>
            <Plus className="relative z-10 w-3 h-3 transition-transform group-hover:rotate-90" strokeWidth={2} />
          </Link>
        </div>
      </div>
    </motion.header>
  );
}

/* =====================================================
   MARQUEE
   ===================================================== */

function MarqueeTicker({ tickers }) {
  const rows = tickers.length ? tickers : [{ symbol: "…", price: 0, change_24h: 0, type: "?" }];
  return (
    <section
      data-testid="hero-ticker"
      className="relative border-y border-white/10 bg-black overflow-hidden select-none"
    >
      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent z-10" />
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black to-transparent z-10" />
      <div className="flex whitespace-nowrap ticker-track py-5">
        {[...rows, ...rows, ...rows].map((t, i) => (
          <div
            key={i}
            className="flex items-center gap-4 px-8 font-mono text-xs uppercase tracking-[0.25em] border-r border-white/5"
          >
            <span className="text-neutral-500">{t.type?.[0]?.toUpperCase()}</span>
            <span className="text-white">{t.symbol}</span>
            <span className="text-neutral-300">{fmtUSD(t.price)}</span>
            <span className={t.change_24h >= 0 ? "pos" : "neg"}>{fmtPct(t.change_24h)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* =====================================================
   MANIFESTO — numbered chapters
   ===================================================== */

const chapters = [
  {
    n: "01",
    title: "One ledger, every asset.",
    body: "Crypto, equities and treasuries live inside the same portfolio. Never rebuild your book across three brokers again — Neo.Invest routes to the highest-scoring provider automatically, and falls back to a deterministic engine so orders always land.",
  },
  {
    n: "02",
    title: "A flat 1% and nothing else.",
    body: "No spread. No maker/taker games. No hidden route markup. Every fee flows to a public ledger the admin can inspect — because a market that hides its cost of capital isn't a market at all.",
  },
  {
    n: "03",
    title: "Fund the way you already pay.",
    body: "Top up with Stripe or PayPal, in sandbox for the demo and live when you deploy. Bigger packs stack bonus credit into the balance. The processor is a swappable provider, not a lock-in.",
  },
];

function Manifesto() {
  return (
    <section id="manifesto" className="relative py-32 md:py-44 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <RevealUp>
          <MetaLine>// manifesto</MetaLine>
          <h2 className="mt-6 text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-none max-w-4xl">
            A quiet terminal for loud markets.
          </h2>
        </RevealUp>

        <div className="mt-24 space-y-24">
          {chapters.map((c, i) => (
            <RevealUp key={c.n} delay={i * 0.05}>
              <article className="grid grid-cols-12 gap-6 items-start">
                <div className="col-span-12 md:col-span-2">
                  <div className="font-mono text-6xl md:text-7xl font-black text-white/10">
                    {c.n}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <h3 className="text-2xl md:text-4xl font-bold tracking-tight leading-tight">
                    {c.title}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <p className="text-neutral-400 leading-relaxed text-base">{c.body}</p>
                </div>
              </article>
            </RevealUp>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =====================================================
   IMAGE GRID — real product photography
   ===================================================== */

const galleryTiles = [
  {
    src: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=70",
    caption: "Terminal night session",
    meta: "01 — book",
    tall: true,
  },
  {
    src: "https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=1200&q=70",
    caption: "Book of executions",
    meta: "02 — ledger",
  },
  {
    src: "https://images.unsplash.com/photo-1642790551116-18e150f248e3?w=1200&q=70",
    caption: "Sunset print",
    meta: "03 — close",
  },
  {
    src: "https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?w=1200&q=70",
    caption: "Treasury desk",
    meta: "04 — bonds",
    tall: true,
  },
];

function ProductGallery() {
  return (
    <section id="gallery" className="relative py-32 md:py-44 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
          <RevealUp>
            <MetaLine>// terminal — dispatches</MetaLine>
            <h2 className="mt-6 text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-none max-w-3xl">
              Field notes from the desk.
            </h2>
          </RevealUp>
          <RevealUp delay={0.1}>
            <p className="text-neutral-400 max-w-sm leading-relaxed">
              A slow-media approach to an over-caffeinated industry. Photographed on shift,
              never staged.
            </p>
          </RevealUp>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {galleryTiles.map((t, i) => (
            <GalleryTile key={i} tile={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryTile({ tile, index }) {
  const [hovered, setHovered] = useState(false);
  const cols = index % 2 === 0 ? "col-span-12 md:col-span-7" : "col-span-12 md:col-span-5";
  return (
    <RevealUp delay={index * 0.05} className={cols}>
      <div
        className="relative overflow-hidden border border-white/10 group cursor-pointer"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ aspectRatio: tile.tall ? "3/4" : "16/10" }}
      >
        <motion.img
          alt={tile.caption}
          src={tile.src}
          className="w-full h-full object-cover grayscale contrast-125"
          animate={{ scale: hovered ? 1.06 : 1 }}
          transition={{ duration: 0.9, ease: easeOutExpo }}
        />
        <motion.div
          className="absolute inset-0 bg-black"
          animate={{ opacity: hovered ? 0.15 : 0.45 }}
          transition={{ duration: 0.5 }}
        />
        <div className="absolute inset-x-0 top-0 p-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
          <span>{tile.meta}</span>
          <AnimatePresence>
            {hovered && (
              <motion.span
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                className="text-white"
              >
                view →
              </motion.span>
            )}
          </AnimatePresence>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
          <div className="font-mono text-xs uppercase tracking-widest text-white">
            {tile.caption}
          </div>
          <motion.div
            className="w-8 h-px bg-white origin-left"
            animate={{ scaleX: hovered ? 3 : 1 }}
            transition={{ duration: 0.6, ease: easeOutExpo }}
          />
        </div>
      </div>
    </RevealUp>
  );
}

/* =====================================================
   BIG EDITORIAL MARQUEE / STATEMENT
   ===================================================== */

function BigStatement() {
  return (
    <section className="relative py-24 md:py-32 border-b border-white/10 overflow-hidden">
      <div className="flex whitespace-nowrap ticker-track" style={{ animationDuration: "80s" }}>
        {["Neo.Invest —", "buy the world at 1% —", "Neo.Invest —", "buy the world at 1% —", "Neo.Invest —", "buy the world at 1% —"].map(
          (t, i) => (
            <span
              key={i}
              className="px-8 text-[16vw] md:text-[10vw] leading-none font-black uppercase tracking-tighter italic text-white/90"
            >
              {t}
            </span>
          ),
        )}
      </div>
    </section>
  );
}

/* =====================================================
   PRICING
   ===================================================== */

const packs = [
  { id: "starter", label: "Starter", amount: 5, bonus: 0 },
  { id: "trader", label: "Trader", amount: 20, bonus: 0.5 },
  { id: "pro", label: "Pro", amount: 100, bonus: 5, featured: true },
  { id: "whale", label: "Whale", amount: 500, bonus: 40 },
];

function Pricing() {
  return (
    <section id="pricing" className="relative py-32 md:py-44 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-4">
          <RevealUp>
            <MetaLine>// credit_packs</MetaLine>
            <h2 className="mt-6 text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-none">
              Top up.<br />Trade. Repeat.
            </h2>
          </RevealUp>
          <RevealUp delay={0.1}>
            <Link
              to="/register"
              data-testid="pricing-cta"
              className="group inline-flex items-center gap-3 border border-white px-7 py-4 font-mono uppercase tracking-widest text-[11px] hover:bg-white hover:text-black transition-colors"
            >
              Start free
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={1.75} />
            </Link>
          </RevealUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-white/10">
          {packs.map((p, i) => (
            <RevealUp
              key={p.id}
              delay={i * 0.06}
              className={`${i < 3 ? "md:border-r border-white/10" : ""} ${
                p.featured ? "bg-white/[0.04]" : ""
              }`}
            >
              <div
                data-testid={`pricing-pack-${p.id}`}
                className="p-8 flex flex-col justify-between h-full min-h-[300px] relative group"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-400">
                      {p.label}
                    </div>
                    {p.featured && (
                      <div className="font-mono text-[9px] uppercase tracking-[0.3em] border border-white/40 px-2 py-0.5 text-white">
                        most funded
                      </div>
                    )}
                  </div>
                  <div className="mt-10 font-mono text-6xl md:text-7xl font-black tracking-tighter leading-none">
                    ${p.amount}
                  </div>
                  <div className="mt-3 text-xs text-neutral-400 font-mono uppercase tracking-widest">
                    {p.bonus > 0 ? `+ $${p.bonus} bonus` : "instant credit"}
                  </div>
                </div>
                <div className="pt-6 border-t border-white/10 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  <span>stripe · paypal</span>
                  <ArrowUpRight
                    className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all"
                    strokeWidth={1.75}
                  />
                </div>
              </div>
            </RevealUp>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =====================================================
   COLOPHON / FOOTER
   ===================================================== */

function Colophon() {
  return (
    <footer className="relative py-20">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-12 gap-6 items-end">
        <RevealUp className="col-span-12 md:col-span-8">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase leading-[0.85]">
            <span className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: easeOutExpo }}
                className="block"
              >
                Ready?
              </motion.span>
            </span>
          </h2>
        </RevealUp>
        <RevealUp delay={0.1} className="col-span-12 md:col-span-4 flex flex-col gap-3 items-start md:items-end">
          <Link
            to="/register"
            className="group inline-flex items-center gap-3 bg-white text-black px-7 py-4 font-mono uppercase tracking-widest text-[11px] border border-white"
          >
            Open account
            <ArrowDownRight
              className="w-4 h-4 rotate-[-90deg] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
              strokeWidth={1.75}
            />
          </Link>
        </RevealUp>

        <div className="col-span-12 mt-14 border-t border-white/10 pt-6 flex flex-wrap items-center justify-between gap-4 text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
          <span>Neo.Invest · demo terminal</span>
          <span>Not investment advice · test environment</span>
        </div>
      </div>
    </footer>
  );
}
